'use client'

import { usePathname, useRouter } from 'next/navigation'
import { parseAsString, useQueryState } from 'nuqs'
import { useCallback, useEffect, useRef, useState } from 'react'

const DEBOUNCE_MS = 500

export function useGlobalSearch() {
	const router = useRouter()
	const pathname = usePathname()
	const isHome = pathname === '/'

	const [urlQuery, setUrlQuery] = useQueryState(
		'q',
		parseAsString.withDefault('').withOptions({ history: 'replace', shallow: true })
	)

	const [inputValue, setInputValue] = useState(urlQuery)
	const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

	// Sync input when URL param changes externally (e.g. back/forward navigation)
	useEffect(() => {
		setInputValue(urlQuery)
	}, [urlQuery])

	const flushToUrl = useCallback(
		(value: string) => {
			if (timerRef.current) {
				clearTimeout(timerRef.current)
				timerRef.current = null
			}
			setUrlQuery(value || null)
		},
		[setUrlQuery]
	)

	const debouncedFlush = useCallback(
		(value: string) => {
			if (timerRef.current) {
				clearTimeout(timerRef.current)
			}
			timerRef.current = setTimeout(() => {
				setUrlQuery(value || null)
				timerRef.current = null
			}, DEBOUNCE_MS)
		},
		[setUrlQuery]
	)

	const handleSearchChange = useCallback(
		(value: string) => {
			setInputValue(value)

			if (!isHome && value.trim()) {
				if (timerRef.current) {
					clearTimeout(timerRef.current)
					timerRef.current = null
				}
				router.push(`/?q=${encodeURIComponent(value)}`)
				return
			}

			debouncedFlush(value)
		},
		[isHome, router, debouncedFlush]
	)

	const handleSearchKeyDown = useCallback(
		(e: React.KeyboardEvent<HTMLInputElement>) => {
			if (e.key === 'Enter') {
				if (isHome) {
					flushToUrl(inputValue)
				} else {
					if (timerRef.current) {
						clearTimeout(timerRef.current)
						timerRef.current = null
					}
					router.push(`/?q=${encodeURIComponent(inputValue)}`)
				}
			}
		},
		[isHome, router, inputValue, flushToUrl]
	)

	const clearSearch = useCallback(() => {
		setInputValue('')
		flushToUrl('')
	}, [flushToUrl])

	return {
		searchValue: inputValue,
		handleSearchChange,
		handleSearchKeyDown,
		clearSearch,
	}
}
